import {
  ArrowRight,
  CheckCircle2,
  Cpu,
  FileText,
  HardDriveDownload,
  LockKeyhole,
  Search,
  ShieldCheck,
  WifiOff,
  X,
  Zap,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ToolCard } from '../components/ToolCard';
import { ToolIcon } from '../components/ToolIcon';
import { toolById, toolCategories, tools, toolsInCategory } from '../domain/tools/registry';
import { localize } from '../domain/tools/types';
import { useLanguage } from '../features/language/useLanguage';
import { useFavorites, useRecentTools } from '../features/preferences/preferences';
import { usePwa } from '../features/pwa/PwaProvider';
import { formatNumber } from '../lib/format';
import { searchTools } from '../lib/search';

export function HomePage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { online, offlineReady } = usePwa();
  const { favorites, toggleFavorite } = useFavorites();
  const recents = useRecentTools();
  const [query, setQuery] = useState('');

  const matches = useMemo(() => searchTools(tools, query, language), [query, language]);
  const favoriteTools = useMemo(() => tools.filter((tool) => favorites.has(tool.id)), [favorites]);
  const recentTools = useMemo(() => recents.map((entry) => toolById.get(entry.id)).filter((tool) => tool !== undefined), [recents]);
  const isSearching = query.trim().length > 0;

  return (
    <>
      <section className="hero">
        <div className="content-wrap hero-grid">
          <div>
            <span className="eyebrow">{t('app.tagline')}</span>
            <h1>{t('app.heroTitle')}</h1>
            <p className="hero-copy">{t('app.heroDescription')}</p>
            <div className="search-shell">
              <Search size={21} aria-hidden="true" />
              <label className="sr-only" htmlFor="home-tool-search">{t('home.searchLabel')}</label>
              <input
                id="home-tool-search"
                className="search-input"
                type="search"
                value={query}
                placeholder={t('home.searchPlaceholder')}
                autoComplete="off"
                onChange={(event) => setQuery(event.currentTarget.value)}
              />
              {isSearching ? (
                <button className="icon-button search-count" type="button" onClick={() => setQuery('')} aria-label={t('home.clearSearch')}>
                  <X size={15} aria-hidden="true" />
                </button>
              ) : (
                <span className="search-count">{formatNumber(tools.length, language)}</span>
              )}
            </div>
            <p className="privacy-note"><ShieldCheck size={17} aria-hidden="true" />{t('app.privacyPromise')}</p>
          </div>

          <div className="hero-proof" aria-label={t('home.privacyTitle')}>
            <div className="proof-top">
              <strong>{t('home.privacyTitle')}</strong>
              <span className="status-pill" data-tone="positive"><span className="status-dot" />{t('common.status.ready')}</span>
            </div>
            <div className="proof-card">
              <div className="proof-file">
                <span className="proof-file-icon"><FileText size={23} aria-hidden="true" /></span>
                <span><strong dir="auto">document.pdf</strong><small>{t('workflow.upload.privacyHint')}</small></span>
                <LockKeyhole size={18} aria-hidden="true" />
              </div>
              <div className="local-flow">
                <div className="flow-step">
                  <span className="flow-step-icon"><Cpu size={18} aria-hidden="true" /></span>
                  <span><strong>{t('workflow.processing.loadingEngine')}</strong><small>{t('app.localProcessing')}</small></span>
                  <CheckCircle2 className="flow-check" size={18} aria-hidden="true" />
                </div>
                <div className="flow-step">
                  <span className="flow-step-icon"><Zap size={18} aria-hidden="true" /></span>
                  <span><strong>{t('workflow.processing.finalizing')}</strong><small>{t('notices.localOnly')}</small></span>
                  <CheckCircle2 className="flow-check" size={18} aria-hidden="true" />
                </div>
                <div className="flow-step">
                  <span className="flow-step-icon"><ShieldCheck size={18} aria-hidden="true" /></span>
                  <span><strong>{t('workflow.processing.validating')}</strong><small>{t('workflow.result.validated')}</small></span>
                  <CheckCircle2 className="flow-check" size={18} aria-hidden="true" />
                </div>
              </div>
              <div className="proof-footer">
                <span>{t('common.status.completedLocally')}</span>
                <CheckCircle2 size={18} aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="content-wrap trust-strip" aria-label={t('home.privacyTitle')}>
        <div className="trust-item">
          <span className="trust-icon"><ShieldCheck size={18} aria-hidden="true" /></span>
          <span><strong>{t('home.privacyTitle')}</strong><small>{t('home.privacyDescription')}</small></span>
        </div>
        <div className="trust-item">
          <span className="trust-icon">{online ? <Zap size={18} aria-hidden="true" /> : <WifiOff size={18} aria-hidden="true" />}</span>
          <span><strong>{online ? t('offline.connectivity.online') : t('offline.connectivity.offline')}</strong><small>{online ? t('offline.connectivity.onlineDescription') : t('offline.connectivity.offlineDescription')}</small></span>
        </div>
        <div className="trust-item">
          <span className="trust-icon"><HardDriveDownload size={18} aria-hidden="true" /></span>
          <span><strong>{offlineReady ? t('offline.readiness.ready') : t('offline.readiness.checking')}</strong><small>{offlineReady ? t('offline.readiness.coreReady') : t('offline.readiness.notReadyDescription')}</small></span>
        </div>
        <div className="trust-item">
          <span className="trust-icon"><Cpu size={18} aria-hidden="true" /></span>
          <span><strong>{t('common.actions.process')}</strong><small>{t('app.localProcessing')}</small></span>
        </div>
      </div>

      {isSearching ? (
        <section className="content-wrap section-block" aria-live="polite">
          <div className="section-heading">
            <div><h2>{t('toolLibrary.title')}</h2><p>{t('toolLibrary.toolCount', { count: matches.length })}</p></div>
          </div>
          {matches.length > 0 ? (
            <div className="tool-grid">{matches.map((tool) => <ToolCard key={tool.id} tool={tool} favorite={favorites.has(tool.id)} onToggleFavorite={toggleFavorite} />)}</div>
          ) : (
            <div className="empty-state"><Search size={32} aria-hidden="true" /><h2>{t('home.noSearchResults', { query })}</h2><button className="button secondary" type="button" onClick={() => setQuery('')}>{t('home.clearSearch')}</button></div>
          )}
        </section>
      ) : (
        <>
          {favoriteTools.length > 0 && (
            <section className="content-wrap section-block">
              <div className="section-heading"><div><h2>{t('home.favoritesTitle')}</h2></div></div>
              <div className="tool-grid">{favoriteTools.slice(0, 6).map((tool) => <ToolCard key={tool.id} tool={tool} favorite onToggleFavorite={toggleFavorite} />)}</div>
            </section>
          )}

          {recentTools.length > 0 && (
            <section className="content-wrap section-block">
              <div className="section-heading"><div><h2>{t('home.recentTitle')}</h2><p>{t('home.recentEmpty')}</p></div></div>
              <div className="tool-grid">{recentTools.slice(0, 6).map((tool) => <ToolCard key={tool.id} tool={tool} favorite={favorites.has(tool.id)} onToggleFavorite={toggleFavorite} />)}</div>
            </section>
          )}

          <section className="content-wrap section-block">
            <div className="section-heading">
              <div><h2>{t('home.categoriesTitle')}</h2><p>{t('home.categoriesDescription')}</p></div>
              <Link className="text-link" to="/tools">{t('navigation.allTools')}<ArrowRight className="directional-icon" size={17} aria-hidden="true" /></Link>
            </div>
            <div className="category-stack">
              {toolCategories.map((category) => (
                <section key={category.id} aria-labelledby={`home-category-${category.id}`}>
                  <div className="category-label">
                    <span className="tool-icon"><ToolIcon category={category.id} /></span>
                    <div><h2 id={`home-category-${category.id}`}>{localize(category.name, language)}</h2><p>{localize(category.description, language)}</p></div>
                  </div>
                  <div className="tool-grid">
                    {toolsInCategory(category.id).map((tool) => <ToolCard key={tool.id} tool={tool} favorite={favorites.has(tool.id)} onToggleFavorite={toggleFavorite} />)}
                  </div>
                </section>
              ))}
            </div>
          </section>
        </>
      )}
    </>
  );
}
